import { useSession } from 'components/auth/useSession'
import { add, Duration } from 'date-fns'
import {
  useLembreteProfissionalByInputQuery,
  useMarcarVisualizadoLembreteProfissionalMutation,
  useReagendarLembreteProfissionalMutation,
} from 'graphql/hooks.generated'
import { LembreteProfissional, LembreteProfissionalIdentificadorEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import { useCallback } from 'react'

interface UseLembreteProfissionalOptions {
  identificador: LembreteProfissionalIdentificadorEnum
  searchBy: LembreteProfissionalSearchBy
}

export enum LembreteProfissionalSearchBy {
  USUARIO,
  ATOR_PAPEL,
}

export interface UseLembreteProfissionalResult {
  loading: boolean
  isAtivo: boolean
  reagendarByData: (data: Instant) => Promise<LembreteProfissional | undefined>
  reagendarByDuration: (duration: Duration) => Promise<LembreteProfissional | undefined>
  marcarVisualizado: () => Promise<LembreteProfissional | undefined>
}

export function useLembreteProfissional(options: UseLembreteProfissionalOptions): UseLembreteProfissionalResult {
  const { identificador, searchBy } = options

  const { data } = useSession()

  const [reagendar] = useReagendarLembreteProfissionalMutation()
  const [marcarVisualizadoMutation] = useMarcarVisualizadoLembreteProfissionalMutation()

  const { getServerTimeNow } = useServerTime()

  const input =
    searchBy === LembreteProfissionalSearchBy.USUARIO
      ? { usuarioId: data?.profissional.usuario.id, identificador }
      : { atorPapelId: data?.acesso?.id, identificador }

  const { data: lembreteProfissionalData, loading } = useLembreteProfissionalByInputQuery({
    variables: { input },
  })

  const lembreteProfissional = lembreteProfissionalData?.lembreteProfissional

  const reagendarByData = useCallback(
    async (dataExibirNovamente: Instant): Promise<LembreteProfissional | undefined> => {
      if (lembreteProfissional) {
        const response = await reagendar({
          variables: {
            input: {
              id: lembreteProfissional.id,
              dataExibirNovamente,
            },
          },
        })
        return response?.data?.reagendarLembreteProfissional
      }
    },
    [lembreteProfissional, reagendar]
  )

  const reagendarByDuration = useCallback(
    async (duration: Duration): Promise<LembreteProfissional | undefined> => {
      if (lembreteProfissional) {
        const response = await reagendar({
          variables: {
            input: {
              id: lembreteProfissional.id,
              dataExibirNovamente: add(getServerTimeNow(), duration).getTime(),
            },
          },
        })
        return response?.data?.reagendarLembreteProfissional
      }
    },
    [lembreteProfissional, reagendar, getServerTimeNow]
  )

  const marcarVisualizado = useCallback(async () => {
    if (lembreteProfissional) {
      const response = await marcarVisualizadoMutation({
        variables: {
          id: lembreteProfissional.id,
        },
      })
      return response?.data?.marcarVisualizadoLembreteProfissional as LembreteProfissional
    }
  }, [lembreteProfissional, marcarVisualizadoMutation])

  const isLembreteNaoVisualizado = !lembreteProfissional?.visualizado
  const isLembreteSemDataExibirNovamente = !lembreteProfissional?.dataExibirNovamente
  const isLembreteComDataExibirPassada =
    !!lembreteProfissional?.dataExibirNovamente && getServerTimeNow() > lembreteProfissional?.dataExibirNovamente

  const isLembreteDeveExibir =
    !loading &&
    !!lembreteProfissional &&
    isLembreteNaoVisualizado &&
    (isLembreteSemDataExibirNovamente || isLembreteComDataExibirPassada)

  return {
    loading,
    isAtivo: isLembreteDeveExibir,
    reagendarByData,
    reagendarByDuration,
    marcarVisualizado,
  }
}
